/**
 * @module lib/commercial/adops/page-targets
 */
import tgamConfig from "config/tgam";
import merge from "lodash/merge";
import isNil from "lodash/isNil";
import { getUserRole, getUserSophiId, getUserHashId, getSophi3Id, getCampaignCodeFromServiceString } from "lib/identity/api";
import { local } from "lib/store";
import { getUrlVars } from "utils/urls";
import { getById, getProp, setProp } from "utils/utils";
import { isMobile } from "utils/detect";
import logger from "utils/logger";
import { getProps as getAdmantxProps } from "lib/commercial/ias-admantx/";
import { getGlobeAdvisorType } from "lib/identity/api";

const debugTag = "[DFP-ADS]";

/**
 * @method getBreakpointTarget
 * @description Determine breakpoint target numeric value.
 * @returns {String}
 */
function getBreakpointTarget() {
  const breakpoints = {
    "320": {
      min: 0,
      max: 767
    },
    "768": {
      min: 768,
      max: 979
    },
    "1024": {
      min: 980,
      max: 1279
    },
    "1280": {
      min: 1280,
      max: 20000
    }
  };
  for (let bp in breakpoints) {
    let mql = window.matchMedia(`(min-width: ${breakpoints[bp].min}px) and (max-width: ${breakpoints[bp].max}px)`);
    if (mql.matches) {
      return bp;
    }
  }
  return "320";
}

/**
 * @method commonPageTargets
 * @description Compose and return common page target values.
 * @returns {Object} All values should be Strings, including Numbers.
 */
function commonPageTargets() {
  const roleId = getUserRole().id || 0;
  const GAType = getGlobeAdvisorType();
  const hashid = getUserHashId();
  const duid = getUserSophiId();
  const sophi3Id = getSophi3Id();
  const campaignCode = getCampaignCodeFromServiceString();
  const testVariant = getProp(tgamConfig, "meta.testVariant", null);
  const rga =
  GAType === "FINANCIAL_ADVISOR" ? "true" : GAType === "INVESTOR" ? "ind" : "false";

  const ret = {
    bp: getBreakpointTarget(),
    rgcg: roleId === 0 ? "n" : roleId.toString(),
    rga: rga,
    mcmid: !duid ? "n" : duid,
    hashid: !hashid ? "n" : hashid,
    duid: !duid ? "n" : duid,
    guid: !window.analyticsLayerGuid ? "n" : window.analyticsLayerGuid,
    duid3: isNil(sophi3Id) ? "n" : sophi3Id,
    scc: !campaignCode ? "n" : campaignCode
  };
  if (testVariant) {
    ret.test = testVariant;
  }
  // ARC-4715 - for article context service (unique per article)
  // Also needs to be set in vast.js - getCustomValues()
  const jsDfpCsJSON = getArticleContextTargets("js-dfp-cs");
  Object.keys(jsDfpCsJSON).forEach(key => {
    ret[key] = jsDfpCsJSON[key].toString();
  });

  return ret;
}

/**
 * @method setupPageTargets
 * @description Combines back-end page target values with front-end values.
 * @returns {Object}
 */
export function setupPageTargets() {
  let frontend = {}; // values for pageTarget
  const blackout = getProp(tgamConfig, "adops.blackout", false);
  const isArticlePage = tgamConfig.meta.isStoryPage || tgamConfig.meta.isVideoPage;
  const queryStringParams = getUrlVars();
  const [admants, categories] = getAdmantxProps(["admants", "categories"]);

  if (blackout) {
    logger.info(`${debugTag} page is under advert blackout, no ads should appear!`);
  }

  if (isArticlePage && !blackout) {
    const clavis = (() => {
      const habits = local.get("tgam.clavis.targeting");
      return (habits && habits.aux_map) ? habits.aux_map : [];
    })();
    let storyAttrs = {
      clh: clavis,
      cms: tgamConfig.env.inAppView === true ? "arcapp" : "arc"
    };
    if (Array.isArray(admants) && admants.length > 0) {
      storyAttrs.ias_admants = admants;
    }
    if (Array.isArray(categories) && categories.length > 0) {
      storyAttrs.ias_categories = categories;
    }
    frontend = merge({}, commonPageTargets(), storyAttrs);
  } else if (!isArticlePage) {
    frontend = commonPageTargets();
  }

  if (!isNil(queryStringParams.adopstest)) {
    frontend.adopstest = String(queryStringParams.adopstest);
  }

  if (!isNil(queryStringParams.subs)) {
    frontend.subs = String(queryStringParams.subs);
  }

  // merge back-end with front-end pageTargets
  // backend page targets are set in inc/default/commercial/adops-page-targets.jsp
  const backend = getProp(tgamConfig, "adops.pageTargets", {});
  const pageTargets = merge({}, backend, frontend);
  setProp(tgamConfig, "adops.pageTargets", pageTargets);
  logger.info(`${debugTag} defining GPT page targets:`, pageTargets);

  // This return object should only be used for JS unit testing.
  // Do not use in production. Get these values via tgamConfig object instead.
  return {
    blackout,
    pageTargets,
    unitPaths: getProp(tgamConfig, "adops.unitPaths", "")
  };
}

/**
 * @method unitPathing
 * @description Determines partial path of the ad unit with the network code and unit code.
 * @returns {String} of partial ad unit patch
 */
export function unitPathing() {
  const isMobileDevice = isMobile();
  let server = isMobileDevice ? "mob.tgam.mobi" : "theglobeandmail.com";

  if (tgamConfig.env.isLocal || tgamConfig.env.isDev) {
    server = "test.theglobeandmail.com";
  }
  return `/58/${server}`;
}

/**
 * @method slotUnitPath
 * @description Determines the full path of the ad unit with the network code and unit code.
 * @returns {String} of full ad unit path
 */
export function slotUnitPath() {
  let iu = getProp(tgamConfig, "adops.unitPaths", "");
  iu = iu === "" ? unitPathing() : `${unitPathing()}/${iu}`;
  return iu;
}

/**
 * @method getArticleContextTargets
 * @description Get the article context service - page targets from an input field
 * @param {String} id - of input field element
 * @returns {Object} - of kvps
 */
export function getArticleContextTargets(id) {
  let ret = {};
  // set DFP values for article context service
  const jsDfpCsString = getById(id);
  if (jsDfpCsString) {
    try {
      ret = JSON.parse(jsDfpCsString.value);
    } catch (e) {
      logger.info(`${debugTag} article context failed`, e);
    }
  }
  return ret;
}
